<template>
  <div class="w-full mt-4 flex flex-col gap-4" style="grid-area: table">
    <template v-if="isLoading">
      <UserTableSkeleton />
    </template>

    <template v-else>
      <div class="overflow-x-auto rounded-lg shadow-sm border border-gray-200 min-h-[300px]">
        <div
          class="hidden md:grid lg:grid-cols-12 md:grid-cols-6 gap-1 w-full items-center px-4 py-2 text-sm text-gray-500 font-semibold"
          style="background: var(--Neutral-LightGray-5, #fafafa)"
        >
          <div class="lg:col-span-3 md:col-span-4 flex">
            <p class="ml-14 text-start">Nome</p>
          </div>
          <div class="lg:col-span-3 hidden lg:block">
            <p class="text-start">Email</p>
          </div>
          <div class="lg:col-span-2 hidden lg:block">
            <p class="text-start">Telefone</p>
          </div>
          <div class="lg:col-span-2 hidden lg:block">
            <p class="text-start">Empresas</p>
          </div>
          <div class="lg:col-span-2 md:col-span-2 text-center">
            <p>Ações</p>
          </div>
        </div>

        <template v-if="users?.data && users.data.length > 0">
          <TableUsers
            v-for="companyUser in paginatedUsers"
            :key="companyUser.user.id"
            :data="companyUser"
            @edit-user="handleOpenEditModal"
          />
        </template>

        <EmptyList v-else title="Nenhum usuário foi encontrado" class="py-12" />
      </div>

      <div v-if="users?.data && users.data.length > 0" class="flex justify-center py-2">
        <v-pagination
          v-model="currentPage"
          :total-visible="3"
          :length="pageCount"
          density="comfortable"
          color="primary"
          @update:model-value="changePage"
        />
      </div>
    </template>

    <EditUserModal
      v-if="showEditUserModal"
      :modalActive="showEditUserModal"
      :userData="userToEdit"
      @close="handleCloseEditModal"
      @user-updated="handleUserUpdated"
    />
  </div>
</template>

<script lang="ts" setup>
import TableUsers from '@/components/designSystem/Table/TableUsers.vue'
import UserTableSkeleton from '@/components/designSystem/Skeleton/UserTableSkeleton.vue'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { storeToRefs } from 'pinia'
import EmptyList from '@/components/EmptyList.vue'
import { ref, computed, watch } from 'vue'
import EditUserModal from './components/UpdateUserModal/Index.vue'
import { CompanyUserResponse } from '@/Network/Types/Responses/CompanyResponses'
import { useErrorToast } from '@/composables/useErrorToast'

const { notifyError } = useErrorToast()
const showEditUserModal = ref(false)
const userToEdit = ref<CompanyUserResponse | null>(null)
const userStore = useUserStore()
const { users, isLoading } = storeToRefs(userStore)

const currentPage = ref(1)
const itemsPerPage = ref(8)

const paginatedUsers = computed(() => {
  if (!users.value?.data) return []
  const start = (currentPage.value - 1) * itemsPerPage.value
  return users.value.data.slice(start, start + itemsPerPage.value)
})

const pageCount = computed(() => {
  if (!users.value?.data) return 0
  return Math.ceil(users.value.data.length / itemsPerPage.value)
})

const changePage = (page: number) => {
  currentPage.value = page
}

watch(
  () => users.value?.data?.length,
  () => {
    currentPage.value = 1
  }
)

const handleOpenEditModal = (userData: CompanyUserResponse) => {
  userToEdit.value = userData
  showEditUserModal.value = true
}

const handleCloseEditModal = () => {
  showEditUserModal.value = false
  userToEdit.value = null
}

const handleUserUpdated = async () => {
  handleCloseEditModal()
  try {
    await userStore.fetchUsers()
  } catch (error) {
    notifyError(error, 'Não foi possível recarregar a lista de usuários.')
  }
}
</script>
